/**
 * Iterator Node Component
 * Container-style group node with dashed border for iterating over collections
 * Supports child nodes and auto-resizing
 */
import React, { memo } from 'react';
import { Handle, Position, NodeProps, NodeResizer, useReactFlow } from 'reactflow';
import { Repeat } from 'lucide-react';
import styles from './Nodes.module.css';

export const IteratorNode: React.FC<NodeProps> = memo(({ data, selected, id }) => {
  const { setNodes } = useReactFlow();
  
  // Calculate minimum dimensions based on child nodes or default
  const minWidth = data.minWidth || 300;
  const minHeight = data.minHeight || 200;

  // Handle resize to update node data
  const onResize = (_: any, params: { width: number; height: number }) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              width: params.width,
              height: params.height,
            },
          };
        }
        return node;
      })
    );
  };

  return (
    <div 
      className={`${styles.iteratorContainer} ${selected ? styles.iteratorSelected : ''} ${data.isDragOver ? styles.iteratorDragOver : ''}`}
      style={{
        width: data.width || minWidth,
        height: data.height || minHeight,
      }}
    >
      {/* Resizer for manual adjustment */}
      <NodeResizer
        color="#3b82f6"
        isVisible={selected}
        minWidth={minWidth}
        minHeight={minHeight}
        onResize={onResize}
        handleStyle={{
          width: 10,
          height: 10,
          borderRadius: 3,
          background: '#3b82f6',
          border: '2px solid white',
        }}
        lineStyle={{
          borderColor: '#3b82f6',
          borderWidth: 2,
        }}
      />
      
      <Handle
        type="target"
        position={Position.Left}
        className={styles.handle}
        style={{ top: '50%' }}
      />
      
      <div className={styles.iteratorHeader}>
        <Repeat size={14} color="#64748b" />
        <span className={styles.nodeLabel} style={{ color: '#64748b', fontSize: '12px' }}>Iterator</span>
        {data.iterKey && (
          <span className={styles.iteratorBadge}>{data.iterKey}</span>
        )}
        {data.childNodeIds && data.childNodeIds.length > 0 && (
          <span className={styles.iteratorCount}>{data.childNodeIds.length} nodes</span>
        )}
      </div>
      
      <div className={styles.iteratorDropZone}>
        {(!data.childNodeIds || data.childNodeIds.length === 0) && (
          <div className={styles.iteratorPlaceholder}>
            Drop nodes here
          </div>
        )}
      </div>
      
      <Handle
        type="source"
        position={Position.Right}
        className={styles.handle}
        style={{ top: '50%' }}
      />
    </div>
  );
});

IteratorNode.displayName = 'IteratorNode';

